import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';

type StatusConfig = {
  label: string;
  iconName?: IconName;
  background: string;
  color: string;
};

type AppointmentStatusVariants =
  | 'appointment-confirmed'
  | 'appointment-unconfirmed'
  | 'appointment-attempted'
  | 'appointment-in-office'
  | 'appointment-completed'
  | 'appointment-no-show'
  | 'appointment-canceled'
  | 'appointment-unknown';

type RequestStatusVariants =
  | 'request-pending'
  | 'request-accepted'
  | 'request-rejected'
  | 'request-messaged'
  | 'request-called'
  | 'request-dismissed'
  | 'request-done'
  | 'request-unknown';

export type StatusBadgeVariants =
  | 'ai-requested'
  | 'ai-added'
  | 'online-requested'
  | 'urgent'
  | AppointmentStatusVariants
  | RequestStatusVariants;

type Props = {
  variant: StatusBadgeVariants;
  iconOnly?: boolean;
};

export const StatusBadge = ({ variant, iconOnly = false }: Props) => {
  const { t } = useTranslation('schedule');

  const { label, iconName, background, color }: StatusConfig = useMemo(() => {
    switch (variant) {
      case 'ai-requested':
        return {
          label: t('AI-Requested'),
          iconName: 'aiassistant-small',
          background: `linear-gradient(100deg, ${theme.colorsV2.blue10()}, ${theme.colorsV2.seaweed5()})`,
          color: 'var(--fabric-color-black)',
        };
      case 'ai-added':
        return {
          label: t('AI-Added'),
          iconName: 'aiassistant-small',
          background: `linear-gradient(100deg, ${theme.colorsV2.blue10()}, ${theme.colorsV2.seaweed5()})`,
          color: 'var(--fabric-color-black)',
        };
      case 'online-requested':
        return {
          label: t('Online Requested'),
          background: theme.colorsV2.indigo50(25),
          color: theme.colorsV2.indigo70(),
        };
      case 'urgent':
        return {
          label: t('Urgent'),
          background: 'var(--fabric-color-red-alpha-50-alpha-25)',
          color: 'var(--fabric-color-red-70)',
          iconName: 'alert',
        };

      // Request Statuses
      case 'request-pending':
        return {
          label: t('Request Pending'),
          background: 'var(--fabric-color-tangerine-alpha-50-alpha-25)',
          color: 'var(--fabric-color-tangerine-70)',
        };
      case 'request-accepted':
        return {
          label: t('Request Accepted'),
          background: 'var(--fabric-color-green-alpha-50-alpha-25)',
          color: 'var(--fabric-color-green-70)',
        };
      case 'request-rejected':
        return {
          label: t('Request Rejected'),
          background: 'var(--fabric-color-red-alpha-50-alpha-25)',
          color: 'var(--fabric-color-red-70)',
        };
      case 'request-messaged':
      case 'request-called':
        return {
          label: t('Contacted'),
          background: 'var(--fabric-color-indigo-alpha-50-alpha-25)',
          color: 'var(--fabric-color-indigo-70)',
        };
      case 'request-dismissed':
        return {
          label: t('Dismissed'),
          background: 'var(--fabric-color-neutral-alpha-100-alpha-10)',
          color: 'var(--fabric-color-neutral-alpha-100-alpha-60)',
        };
      case 'request-done':
        return {
          label: t('Done'),
          iconName: 'aiassistant-small',
          background: 'var(--fabric-color-red-alpha-100-alpha-20)',
          color: 'var(--fabric-color-neutral-alpha-100-alpha-75)',
        };
      case 'request-unknown':
        return {
          label: t('Other'),
          background: 'var(--fabric-color-eggplant-alpha-50-alpha-25)',
          color: 'var(--fabric-color-eggplant-70)',
        };

      // Appointment Statuses
      case 'appointment-unconfirmed':
        return {
          label: t('Appointment Unconfirmed'),
          background: 'var(--fabric-color-yellow-alpha-20-alpha-25)',
          color: 'var(--fabric-color-yellow-70)',
        };
      case 'appointment-confirmed':
        return {
          label: t('Appointment Confirmed'),
          background: 'var(--fabric-color-blue-alpha-50-alpha-25)',
          color: 'var(--fabric-color-blue-70)',
        };
      case 'appointment-attempted':
        return {
          label: t('Attempted contact'),
          background: 'var(--fabric-color-indigo-alpha-50-alpha-25)',
          color: 'var(--fabric-color-indigo-70)',
        };
      case 'appointment-in-office':
        return {
          label: t('In-Office'),
          background: 'var(--fabric-color-seaweed-alpha-50-alpha-25)',
          color: 'var(--fabric-color-seaweed-70)',
        };
      case 'appointment-completed':
        return {
          label: t('Completed'),
          background: 'var(--fabric-color-green-alpha-50-alpha-25)',
          color: 'var(--fabric-color-green-70)',
        };
      case 'appointment-no-show':
        return {
          label: t('No-show'),
          background: 'var(--fabric-color-red-alpha-50-alpha-25)',
          color: 'var(--fabric-color-red-70)',
        };
      case 'appointment-canceled':
        return {
          label: t('Canceled'),
          background: 'var(--fabric-color-red-alpha-50-alpha-25)',
          color: 'var(--fabric-color-red-70)',
        };
      case 'appointment-unknown':
        return {
          label: t('Unknown'),
          background: 'var(--fabric-color-neutral-alpha-100-alpha-10)',
          color: 'var(--fabric-color-neutral-alpha-100-alpha-20)',
        };
      default:
        return {
          label: '',
          background: 'var(--fabric-color-neutral-00)',
          color: 'var(--fabric-color-neutral-100)',
        };
    }
  }, [variant, t]);

  const hasIconAndLabel = !!iconName && !iconOnly;

  return (
    <div css={statusBadgeStyles(background, color, hasIconAndLabel)}>
      {iconName && <Icon name={iconName} size={16} css={{ flexShrink: 0 }} />}
      {!iconOnly && <span>{label}</span>}
    </div>
  );
};

const statusBadgeStyles = (background: string, color: string, hasIconAndLabel: boolean) => css`
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing(0.5)};
  background: ${background};
  color: ${color};
  padding: ${hasIconAndLabel ? theme.spacing(0.5) : theme.spacing(0.25, 0.5)};
  border-radius: ${theme.borderRadius.small};
  font-size: ${theme.font.size.small};
`;
